import React, { useEffect, useRef } from 'react';
import {
  Animated,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MenuPanel } from './MenuPanel';
import { spacing } from '../constants/spacing';
import { drawerWidthFor } from '../utils/responsiveLayout';
import { typography } from '../constants/typography';
import { useMenuDrawer } from '../context/MenuDrawerContext';
import { usePendingRequestCount } from '../context/PendingRequestCountContext';
import { useAppColors } from '../context/PreferencesContext';

export function MenuDrawer() {
  const { isOpen, closeMenuDrawer } = useMenuDrawer();
  const pendingRequests = usePendingRequestCount();
  const reloadPendingCounts = pendingRequests.reloadPendingCounts;
  const palette = useAppColors();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const drawerWidth = drawerWidthFor(width);
  const slide = useRef(new Animated.Value(drawerWidth)).current;

  useEffect(() => {
    if (isOpen) {
      Promise.resolve(reloadPendingCounts()).catch(() => undefined);
      slide.setValue(drawerWidth);
      Animated.timing(slide, {
        toValue: 0,
        duration: 220,
        useNativeDriver: true,
      }).start();
    }
  }, [isOpen, drawerWidth, reloadPendingCounts, slide]);

  function close() {
    Animated.timing(slide, {
      toValue: drawerWidth,
      duration: 180,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) closeMenuDrawer();
    });
  }

  return (
    <Modal
      visible={isOpen}
      transparent
      animationType="fade"
      onRequestClose={close}
      statusBarTranslucent
    >
      <View style={styles.root}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close menu"
          style={[styles.backdrop, { backgroundColor: 'rgba(0,0,0,0.45)' }]}
          onPress={close}
        />
        <Animated.View
          style={[
            styles.drawer,
            {
              width: drawerWidth,
              backgroundColor: palette.surface,
              paddingTop: insets.top + spacing.md,
              paddingBottom: insets.bottom + spacing.lg,
              transform: [{ translateX: slide }],
            },
          ]}
        >
          <View style={styles.header}>
            <Text
              accessibilityRole="header"
              style={[styles.title, { color: palette.textPrimary }]}
            >
              Menu
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close menu"
              onPress={close}
              hitSlop={8}
              style={styles.closeBtn}
            >
              <Text style={[styles.closeGlyph, { color: palette.textPrimary }]}>
                ✕
              </Text>
            </Pressable>
          </View>
          <ScrollView
            contentContainerStyle={styles.scroll}
            showsVerticalScrollIndicator={false}
          >
            <MenuPanel onNavigate={close} />
          </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  drawer: {
    height: '100%',
    paddingHorizontal: spacing.lg,
    zIndex: 2,
    elevation: 8,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 12,
    shadowOffset: { width: -2, height: 0 },
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  title: {
    ...typography.sectionTitle,
    fontWeight: '700',
  },
  closeBtn: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeGlyph: {
    fontSize: 18,
    fontWeight: '600',
  },
  scroll: {
    paddingBottom: spacing.xl,
  },
});
